import React, { useState, useEffect } from 'react';
import { getPolyMarket } from '../../services/api';
import './PolymarketBuzz.css';

const PolymarketLogo = ({ size = 20 }) => (
  <svg viewBox="0 0 136 168" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: size }}>
    <path d="M136.267 152.495C136.267 159.76 136.267 163.392 133.891 165.192C131.516 166.993 128.019 166.012 121.024 164.049L8.63192 132.51C4.41793 131.328 2.31093 130.737 1.09248 129.129C-0.125977 127.522 -0.125977 125.333 -0.125977 120.957V47.0434C-0.125977 42.6667 -0.125977 40.4783 1.09248 38.8709C2.31093 37.2634 4.41792 36.6722 8.63191 35.4897L121.024 3.95096C128.019 1.98834 131.516 1.00703 133.891 2.80771C136.267 4.60839 136.267 8.24049 136.267 15.5047V152.495ZM27.9043 122.228L120.966 148.345V96.1133L27.9043 122.228ZM15.1738 110.111L108.217 84L15.1738 57.8887V110.111ZM27.9033 45.7725L120.966 71.8877V19.6553L27.9033 45.7725Z" fill="currentColor" />
  </svg>
);

const MAX_CARDS = 8;

// 백엔드 응답 스키마:
// { markets: [{ eventId, eventSlug, eventTitle, eventStartDate, eventEndDate,
//               eventVolume, eventVolume24hr, eventLiquidity,
//               marketId, marketSlug, question, groupItemTitle, marketEndDate,
//               outcomes: ["Up","Down"], outcomePrices: [0.205, 0.795],
//               lastTradePrice, bestBid, bestAsk, spread }, ...] }

const formatVolume = (vol) => {
  if (vol == null || vol <= 0) return null;
  if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `$${(vol / 1_000).toFixed(0)}K`;
  return `$${Math.round(vol)}`;
};

const MarketCard = ({ market }) => {
  // outcomePrices는 이미 number 배열 [yesPrice, noPrice] 형태
  const prices = Array.isArray(market.outcomePrices) ? market.outcomePrices : [];
  const yesPrice = Number(prices[0]);
  const noPrice = Number(prices[1]);

  if (Number.isNaN(yesPrice) || Number.isNaN(noPrice)) return null;

  const yesPercent = Math.round(yesPrice * 100);
  const noPercent = Math.round(noPrice * 100);

  const outcomes = Array.isArray(market.outcomes) ? market.outcomes : ['Yes', 'No'];
  const yesLabel = outcomes[0] || 'Yes';
  const noLabel = outcomes[1] || 'No';

  const title = market.eventTitle || market.question || market.groupItemTitle || '';
  const slug = market.eventSlug || market.marketSlug;
  const volumeStr = formatVolume(market.eventVolume24hr ?? market.eventVolume);

  const handleClick = () => {
    if (slug) window.open(`https://polymarket.com/event/${slug}`, '_blank');
  };

  return (
    <div className="pmb-card" onClick={handleClick}>
      <p className="pmb-title">{title}</p>

      <div className="pmb-prob-labels">
        <span className="pmb-prob-yes">{yesPercent}%</span>
        <span className="pmb-prob-no">{noPercent}%</span>
      </div>

      <div className="pmb-bar-wrap">
        <div className="pmb-bar-yes" style={{ width: `${yesPercent}%` }} />
        <div className="pmb-bar-no" style={{ width: `${noPercent}%` }} />
      </div>

      <div className="pmb-footer">
        <span className="pmb-label-yes">{yesLabel}</span>
        <span className="pmb-label-no">{noLabel}</span>
        {volumeStr && <span className="pmb-volume">24h {volumeStr}</span>}
      </div>
    </div>
  );
};

const SkeletonCards = () => (
  <div className="pmb-scroll">
    {[0, 1, 2].map((i) => (
      <div key={i} className="pmb-card pmb-skeleton" />
    ))}
  </div>
);

const PolymarketBuzz = () => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPolyMarket();
        if (cancelled) return;
        const list = Array.isArray(data?.markets) ? data.markets : [];

        // 가격 데이터가 유효한 항목만, 24h 거래량 내림차순(없으면 뒤로)
        const filtered = list
          .filter((m) => {
            const p = Array.isArray(m.outcomePrices) ? m.outcomePrices : [];
            return p.length >= 2 && !Number.isNaN(Number(p[0])) && !Number.isNaN(Number(p[1]));
          })
          .sort((a, b) => (b.eventVolume24hr ?? 0) - (a.eventVolume24hr ?? 0))
          .slice(0, MAX_CARDS);

        setMarkets(filtered);
      } catch (err) {
        console.error('[PolymarketBuzz] fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="feature-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="feature-icon pmb-icon">
          <PolymarketLogo size={20} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-black">Polymarket 예측</h2>
            <span className="edu-fun-badge">스낵 콘텐츠</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">실제 돈이 베팅된 시장 확률</p>
        </div>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        Yes/No 확률은 시장 참여자들의 집단 예측을 반영하며, 거래량이 높을수록 신뢰도가 높습니다.
      </p>

      {loading ? (
        <SkeletonCards />
      ) : markets.length === 0 ? null : (
        <div className="pmb-scroll">
          {markets.map((m) => (
            <MarketCard key={m.eventId || m.marketId || m.eventSlug} market={m} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PolymarketBuzz;
